/**
 * Pure logic for switching audio tracks on a Chromecast receiver.
 *
 * castable-video only mirrors text tracks to the cast device (it listens to
 * `textTracks` changes and sends EditTracksInfoRequest); audio selection has
 * no out-of-the-box bridge. bunny-stream-video listens for local
 * `audioTracks` changes and uses these helpers to translate the selection
 * into receiver track IDs. Matching is by language (exact, then primary
 * subtag), then by name, because local hls.js track order is not guaranteed
 * to match the receiver's manifest parse order.
 */

export interface ReceiverTrack {
  trackId: number;
  type?: string | null;
  language?: string | null;
  name?: string | null;
}

/** The chrome.cast.media.Media surface the cast bridges touch. */
export interface CastMediaSessionLike {
  mediaSessionId?: number;
  activeTrackIds?: number[] | null;
  playbackRate?: number;
  playerState?: string;
  idleReason?: string | null;
  media?: { tracks?: ReceiverTrack[] | null } | null;
  editTracksInfo(
    request: unknown,
    onSuccess: () => void,
    onError: (error: unknown) => void,
  ): void;
}

function isAudioTrack(track: ReceiverTrack): boolean {
  return typeof track.type === 'string' &&
    track.type.toUpperCase() === 'AUDIO';
}

function primarySubtag(language: string): string {
  return language.split('-')[0];
}

/**
 * Pick the receiver audio track matching a locally selected track.
 * Returns null when there is nothing meaningful to switch to.
 * When several receiver tracks share a language (e.g. stereo + 5.1) the
 * first one wins — the sender has no channel-layout information to
 * disambiguate further.
 */
export function pickReceiverAudioTrack(
  tracks: ReceiverTrack[] | null | undefined,
  language: string | null | undefined,
  label: string | null | undefined,
): ReceiverTrack | null {
  const audioTracks = (tracks ?? []).filter(isAudioTrack);
  if (audioTracks.length === 0) return null;

  const wantedLanguage = language?.trim().toLowerCase() || null;
  if (wantedLanguage) {
    const exact = audioTracks.find((track) =>
      track.language?.toLowerCase() === wantedLanguage
    );
    if (exact) return exact;

    const wantedPrimary = primarySubtag(wantedLanguage);
    const primary = audioTracks.find((track) =>
      track.language &&
      primarySubtag(track.language.toLowerCase()) === wantedPrimary
    );
    if (primary) return primary;
  }

  const wantedLabel = label?.trim().toLowerCase() || null;
  if (wantedLabel) {
    const byName = audioTracks.find((track) =>
      track.name?.trim().toLowerCase() === wantedLabel
    );
    if (byName) return byName;
  }

  return null;
}

/**
 * Build the replacement active-track-id list for an EditTracksInfoRequest.
 * The request replaces the entire active set, so everything that is not an
 * audio track (text tracks, unknown IDs) must be preserved or captions would
 * be switched off as a side effect of changing the audio language.
 */
export function buildActiveTrackIds(
  tracks: ReceiverTrack[] | null | undefined,
  currentActiveIds: number[] | null | undefined,
  newAudioTrackId: number,
): number[] {
  const audioTrackIds = new Set(
    (tracks ?? []).filter(isAudioTrack).map((track) => track.trackId),
  );

  const preserved = (currentActiveIds ?? []).filter((id) =>
    !audioTrackIds.has(id)
  );

  return [...preserved, newAudioTrackId];
}
